import { Preferences } from '@3ds/common-preferences'

import type { IChildWindow } from '~/features-swym/multi-window/interfaces/multi-window'

import { IContentModel } from '~/features-shared/content/interfaces/content-model'

import { useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'
/**
 * openNewWindowWithUrl: This function triggers to open new native window in native application
 * The native window opened by native application will open navigate with winUrl if it's defined
 * Otherwise the native window opened by teleport a Vue3 component.
 * @param options   the options
 * @param options.winId   : Unique ID for window
 * @param options.winUrl  : URL (optional)
 * @param options.winOpts : Configuration for new window, such as width, height, position, etc.
 * @returns the opened window reference
 */
export function openNewWindowWithUrl({ winId, winUrl, winOpts }: {
  winId: string
  winUrl: string
  winOpts: string
}, isChild = true): Window | null {
  let newWindUrl = winUrl
  if (isChild) {
    newWindUrl = getMultiWindowPath(winUrl)
  }
  // HTP1: Emit newWindow event to native app, e.g. iOS to manage new window
  // CommonEvents.emitEventToNativeApp('newWindow', { url: winUrl, options: winOpts, id: winId })
  // Call window.open to trigger open new window in native app
  const w = window.open(newWindUrl, '_blank', `popup=true,${winOpts},config=${JSON.stringify({ url: winUrl, options: winOpts, id: winId })}`)
  return w
}

/**
 * Check if it's apple vision
 * First check if it's not a mobile device, then if it isn't desktop app but it's native app it's the apple vision
 */
export const isAppleVision = computed(() => {
  const isMobileDevice = useFeatureManager('app.config.mobile_device')
  if (isMobileDevice.value)
    return false
  if (Preferences.isDesktopApp())
    return false

  else if (Preferences.isNativeApp())
    return true

  return false
})

/**
 * get the id of the child window
 * @param path
 * @param contentUri
 * @returns the multiWindow id
 */
export function getMultiWindowId(path: string, contentUri?: string): string {
  if (contentUri !== undefined)
    return contentUri
  // this will return the subject_uri of the community or the subject_uri with the filter
  return path.includes('/dm/') ? path.split('/dm/')[1] : path.split('/community/')[1]
}

/**
 * Get child path for opening a content or a media in a new window
 * @param subjectUri
 * @param model
 * @returns the path to create the child window for content and media
 */
export function getMultiWindowPathForContent(subjectUri: string, model: IContentModel): string {
  if (model === IContentModel.Media)
    return `/home?media=${subjectUri}`

  return `/home?content=${subjectUri}`
}

/**
 * Get child path for opening a community filtered or not in a new window
 * @param subjectUri
 * @param filter
 * @returns the path to create the child window for community filtered or not
 */
export function getMultiWindowPathForCommunity(subjectUri: string, filter?: string): string {
  const link = `/community/${subjectUri}`
  if (filter !== undefined)
    return `${link}/${filter}`
  return link
}

/**
 * Get path for opening whatever content on a new window
 * @params path path of the content
 */
export function getMultiWindowPath(url: string) {
  if (Preferences.isNativeApp())
    return url.includes('?') ? `${url}&isChild=true` : `${url}?isChild=true`
  return url
}

/**
 * Remove from the path used for multi window every parameter that are not use full (mainly use for multiwindow in NAtive apps)
 * @param url : url of the child Window
 */
export function refineMultiWindowPath(url: string): string {
  return !url.includes('isChild=true')
    ? url
    : url.includes('&isChild=true')
      ? url.replace('&isChild=true', '')
      : url.replace('?isChild=true', '')
}

/**
 * Get child path for opening a dm filterred or not
 * @param subjectUri
 * @returns the path to create the child window for community filtered or not
 */
export function getMultiWindowPathForDm(subjectUri: string): string {
  return `/dm/${subjectUri}`
}

/**
 * check if the current window is a child window
 */
export const isMultiWindowActive = computed(
  () => (window as IChildWindow)?.isChild || sessionStorage.getItem('isChild') === 'true',
)
